import InputComponent from "../../components/input/input.component";
import TextareaComponent from "../../components/textarea/textarea.component";
import { useFieldArray, useForm } from "react-hook-form";
import usePeriod from "../../hooks/period.use";
import { useEffect } from "react";
import { UtilsService } from "../../services/utils";
import Button from "../../components/button/button.component";
import { useNavigate } from "react-router-dom";
import { to } from "../../polyfill";
import { RegistersService } from "../../services/operation/registers";

function Step2({ corte, prev, next }: any) {
  const navigate = useNavigate();

  const { currentPeriod } = usePeriod();

  type FormValues = {
    _id: string;
    period: string;
    customers: {
      index: number;
      customer: string;
      customer_name: string;
      field: string;
      field_name: string;
      cajas: number;
      peso_caja: number;
      kg_totales: number;
      valor_total: string;
    }[];
    packing: {
      folio_corrida: string;
      valor_neto: string;
      precio_kg: string;
      ajuste_valor_extra: string;
      observaciones: string;
    };
  };

  const defaultValues: FormValues = {
    _id: "",
    period: currentPeriod._id,
    customers: [],
    packing: {
      folio_corrida: "",
      valor_neto: "",
      precio_kg: "",
      ajuste_valor_extra: "",
      observaciones: "",
    },
  };

  const {
    control,
    register,
    reset,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<typeof defaultValues>({
    mode: "onChange",
    defaultValues,
  });

  const _id = watch("_id");
  const folio_corrida = watch("packing.folio_corrida");

  const { fields, prepend } = useFieldArray<any>({
    control,
    name: "customers",
    rules: {
      minLength: 1,
    },
  });

  const submit = async (form: typeof defaultValues) => {
    const [error, data] = await to(RegistersService.saveStep2(form));
    if (error) {
      alert(error.message);
    } else {
      navigate("/register");
    }
  };

  useEffect(() => {
    if (corte) {
      reset({
        _id: corte._id,
        packing: {
          folio_corrida: corte?.packing?.folio_corrida || "",
          valor_neto: corte?.packing?.valor_neto || 0,
          precio_kg: corte?.packing?.precio_kg || 0,
          ajuste_valor_extra: corte?.packing?.ajuste_valor_extra || 0,
          observaciones: corte?.packing?.observaciones || "",
        },
      });
      const customers = corte.customers.map((customer: any, index: number) => ({
        _id: customer._id,
        index: index++,
        customer: customer.customer._id,
        customer_name: customer.customer.name,
        field: customer.field._id,
        field_name: customer.field.name,
        cajas: customer.cajas,
        peso_caja: customer.peso_caja,
        kg_totales: customer.kg_totales,
        valor_total: customer.valor_total,
      }));

      setTimeout(() => prepend(customers), 100);
    }
  }, [corte]);

  return (
    <>
      <div className="pb-10 pb-lg-15">
        <h2 className="fw-bold d-flex align-items-center text-gray-900">
          Empaque
        </h2>
        <div className="text-muted fw-semibold fs-6">
          Ingresa la información general del corte, además de registrar las
          cajas por productor.
        </div>
      </div>

      <div className="row mb-5">
        <div className="col-6">
          <div className="">
            <InputComponent
              label="Folio de corrida *"
              {...register("packing.folio_corrida", { required: true })}
              errors={errors.packing?.folio_corrida}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="">
            <InputComponent
              label="Valor neto de fruta *"
              {...register("packing.valor_neto", {
                required: true,
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message:
                    "Solo se permiten números y un máximo de dos decimales",
                },
              })}
              errors={errors.packing?.valor_neto}
            />
          </div>
        </div>
      </div>
      <div className="row mb-5">
        <div className="col-6">
          <div className="">
            <InputComponent
              label="Precio por kg *"
              {...register("packing.precio_kg", {
                required: true,
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message:
                    "Solo se permiten números y un máximo de dos decimales",
                },
              })}
              errors={errors.packing?.precio_kg}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="">
            <InputComponent
              label="Ajuste a valor extra *"
              {...register("packing.ajuste_valor_extra", {
                required: true,
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message:
                    "Solo se permiten números y un máximo de dos decimales",
                },
              })}
              errors={errors.packing?.ajuste_valor_extra}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="">
            <TextareaComponent
              label="Observaciones por valor extra *"
              {...register("packing.observaciones", { required: true })}
              errors={errors.packing?.observaciones}
            />
          </div>
        </div>
      </div>

      <div className="separator my-10"></div>

      <h4 className="fw-bold d-flex align-items-center text-gray-900 mb-5">
        Registro por productor
      </h4>
      {/* <p className="text-muted fw-semibold fs-6">Lorem ipsum</p> */}

      <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-6 mb-5">
        <i className="ki-duotone ki-design-1 fs-2tx text-primary me-4" />
        <div className="d-flex flex-stack flex-grow-1">
          <div className="fw-semibold">
            <div className="fs-6 text-gray-700">
              Ingresa el valor total de cada productor
            </div>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover table-rounded table-striped border gy-7 gs-7">
          <thead>
            <tr className="fw-semibold fs-6 text-gray-800 border-bottom-2 border-gray-200">
              <th>Productor</th>
              <th>Huerta</th>
              <th>Cajas</th>
              <th>Peso x caja</th>
              <th>Kg totales</th>
              <th>Precio x kg</th>
              <th>Valor total</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field: any, index: number) => (
              <tr key={field.id}>
                <td className="align-middle">{field.customer_name}</td>
                <td className="align-middle">{field.field_name}</td>
                <td className="align-middle">
                  {UtilsService.numberFormat(field.cajas)}
                </td>
                <td className="align-middle">
                  {UtilsService.numberFormat(field.peso_caja)}
                </td>
                <td className="align-middle">
                  {UtilsService.numberFormat(field.kg_totales)}
                </td>
                <td className="align-middle">
                  $
                  {UtilsService.numberFormat(
                    +watch(`customers.${index}.valor_total`) / +field.kg_totales
                  )}
                </td>
                <td className="align-middle">
                  <InputComponent
                    label=""
                    {...register(`customers.${index}.valor_total`, {
                      required: true,
                      pattern: {
                        value: /^\d+(\.\d{1,2})?$/,
                        message:
                          "Solo se permiten números y un máximo de dos decimales",
                      },
                    })}
                    errors={errors.customers?.[index]?.valor_total}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex mt-8">
        {_id && corte?.packing?.folio_corrida && (
          <div className="">
            <button
              className="btn btn-lg btn-light-primary me-3"
              type="button"
              onClick={prev}
            >
              <i className="ki-duotone ki-arrow-left fs-4 me-1">
                <span className="path1" />
                <span className="path2" />
              </i>
              Regresar
            </button>
          </div>
        )}

        <div className="ms-auto ">
          {/* {
            _id && corte?.packing?.folio_corrida ? (
              <Button
                className="btn btn-primary"
                disabled={!isValid}
                isLoading={isSubmitting}
                onClick={next}
              >
                Siguiente
                <i className="ki-duotone ki-arrow-right fs-4 ms-1 me-0">
                  <span className="path1" />
                  <span className="path2" />
                </i>
              </Button>
            ) : (
              <Button
                className="btn btn-primary"
                disabled={!isValid}
                isLoading={isSubmitting}
                onClick={handleSubmit(submit)}
              >
                Guardar
              </Button>
            )
          } */}
          <Button
            className="btn btn-primary"
            disabled={!isValid}
            isLoading={isSubmitting}
            onClick={handleSubmit(submit)}
          >
            Guardar
          </Button>
        </div>
      </div>
    </>
  );
}

export default Step2;
